// ---------------------------------------------------------------------------
// Delta-pull cursor arithmetic. Pure, so the rule can be tested directly.
// ---------------------------------------------------------------------------

/**
 * Longest a writing transaction is assumed to stay open.
 *
 * Postgres stamps updated_at and received_at with now(), which is the
 * transaction's START time. A transaction still open while a pull runs
 * commits afterwards carrying a timestamp older than rows the pull already
 * returned, and a cursor sitting exactly on the newest row would skip it for
 * good. So the cursor only moves past a timestamp once every transaction that
 * could still produce one has had this long to commit.
 *
 * A minute is generous: a push batch, an RPC and an ingest batch each run in
 * well under a second.
 */
export const OVERLAP_MS = 60_000;

/** One table's position in the delta pull. */
export interface PullPosition {
  /** Exclusive lower bound for the next query: everything at or below it is collected. */
  cursor: string;
  /** Newest timestamp any pull has returned. */
  newest: string;
  /**
   * Device clock, in ms, when `newest` was first returned. Only ever compared
   * with another reading of the same clock, so a phone set to the wrong time
   * of day does not matter -- only how much time has passed.
   */
  newestSeenAt: number;
}

export function freshPosition(cursor: string): PullPosition {
  return { cursor, newest: cursor, newestSeenAt: 0 };
}

/**
 * A stored position. `stored` is the JSON `serializePosition` wrote, kept
 * beside the cursor rather than in place of it, so a device that has only
 * ever written the bare cursor upgrades without a re-pull.
 */
export function readPosition(cursor: string, stored: string | null): PullPosition {
  try {
    const parsed = stored ? (JSON.parse(stored) as Partial<PullPosition>) : null;
    if (typeof parsed?.newest === 'string' && typeof parsed.newestSeenAt === 'number') {
      return { cursor, newest: parsed.newest, newestSeenAt: parsed.newestSeenAt };
    }
  } catch {
    // Unreadable is the same as absent: start from the cursor alone.
  }
  return freshPosition(cursor);
}

export function serializePosition(p: PullPosition): string {
  return JSON.stringify({ newest: p.newest, newestSeenAt: p.newestSeenAt });
}

/**
 * Microseconds since the epoch. Compared as instants, not strings: Postgres
 * returns `2026-09-08T01:08:01.27577+00:00` and JS produces
 * `2026-09-08T01:08:01.275Z`, and lexicographic comparison of those two gets
 * the wrong answer, because `'7'` sorts below `'Z'`.
 *
 * Microseconds rather than Date.parse's milliseconds, because a cursor that
 * has caught up has to sit exactly on the newest row. Truncated to
 * `.275Z`, `gt(updated_at, cursor)` still matches the `.27577` row, and a
 * whole turf stamped by one redraw comes back on every pull.
 */
export function micros(iso: string): number {
  // The fraction is parsed here rather than by Date.parse, whose handling of
  // more than three digits -- truncate or round -- is the engine's choice.
  const fraction = /T[^.]*\.(\d+)/.exec(iso)?.[1] ?? '';
  const seconds = Date.parse(iso.replace(/(T[^.]*)\.\d+/, '$1'));
  if (!Number.isFinite(seconds)) return 0;
  return seconds * 1000 + Number((fraction + '000000').slice(0, 6));
}

/**
 * Where one table's pull leaves it, given the timestamps its query returned
 * and when that query started and finished on the device clock.
 *
 * The cursor moves to the furthest of three points, and never backwards:
 *
 *   * Where it already is.
 *   * The newest row less the overlap, less however long this pull took. A
 *     row that old committed before this pull's first page was read, so every
 *     page could see it.
 *   * The newest row from BEFORE this pull, once this pull started at least
 *     the overlap after that row was first seen. Nothing at or below it can
 *     still be in flight.
 *
 * The third is what lets a quiet table go quiet. With only the second, the
 * last minute of rows was re-fetched on every pull until something newer
 * arrived -- forever, for a table nobody was writing to. A turf redraw stamps
 * every household with one timestamp, so the whole turf, and its voter file,
 * came back every 25 seconds.
 *
 * `fresh` counts rows newer than anything seen before, which is the honest
 * measure of someone else working. Rows re-read inside the overlap are not.
 */
export function advance(
  position: PullPosition,
  stamps: readonly string[],
  startedAt: number,
  finishedAt: number,
): { position: PullPosition; fresh: number } {
  const before = micros(position.newest);
  let newest = position.newest;
  let newestAt = before;
  let fresh = 0;
  for (const stamp of stamps) {
    const t = micros(stamp);
    if (t > before) fresh++;
    if (t > newestAt) {
      newest = stamp;
      newestAt = t;
    }
  }

  // The rewound point is computed, so it is rounded DOWN to the millisecond:
  // re-reading a sliver more is safe, and skipping a sliver is not. The
  // settled point is a row's own stamp, kept verbatim so it is exact.
  const span = Math.max(0, finishedAt - startedAt);
  const rewound = new Date(Math.floor((newestAt / 1000) - OVERLAP_MS - span)).toISOString();
  const settled = startedAt - position.newestSeenAt >= OVERLAP_MS;

  let cursor = position.cursor;
  for (const candidate of settled ? [rewound, position.newest] : [rewound]) {
    if (micros(candidate) > micros(cursor)) cursor = candidate;
  }

  return {
    position: {
      cursor,
      newest,
      newestSeenAt: newestAt > before ? finishedAt : position.newestSeenAt,
    },
    fresh,
  };
}
